import requestAPI from '@/logic/requestBeApi';
import store from '@/store';

const trainingService = {
  async getTrainings({
    self,
    filter,
    reloadExisting,
  }: {
    self: any;
    filter: string;
    reloadExisting?: boolean;
  }): Promise<void> {
    if (store.state.allTrainingsDelivered && !reloadExisting) return;
    store.commit('setTrainingsRequestPending', true);
    const currentListLength = store.state.eventsNumber.trainings[filter];
    const defaultCall = !reloadExisting;
    let page: number, perPage: number;
    if (defaultCall) {
      perPage = 10;
      page = store.state.nextTrainingsPageNum;
    } else if (reloadExisting) {
      perPage = currentListLength + 1;
      page = 1;

      store.commit('cleanTrainings');
    }
    let statuses: string;
    if (filter === 'overall')
      statuses =
        'status[]=in_progress&status[]=new&status[]=cancelled&status[]=completed&status[]=post_work&status[]=pre_work';
    if (filter === 'active') statuses = 'status[]=in_progress&status[]=new&status[]=pre_work';
    if (filter === 'complete')
      statuses = 'status[]=cancelled&status[]=completed&status[]=post_work';
    if (defaultCall)
      store.commit('setNextTrainingsPageNum', (store.state.nextTrainingsPageNum += 1));
    const response = await requestAPI({
      url: `/api/v1/trainings?${statuses}&page=${page}&per_page=${perPage}&order[updated_at]=DESC`,
      method: 'GET',
    });

    if (response.ok) {
      this.parseEvents(response, 'trainings', filter);
    } else if (response.status !== 401) self.$message.error(self.$t('someErrorOccurred'));

    store.commit('setTrainingsRequestPending', false);
  },

  async parseEvents(response: { json: () => any }): Promise<void> {
    const data = await response.json();

    const events: any[] = [];

    data.forEach((training: any) => {
      const evaluations = training.evaluations || [];
      const trainingModulesData = training.training_modules || [];
      const trainingId = training.id;

      const trainingEvent = {
        id: training.id,
        name: training.name,
        description: training.description,
        status: training.status,
        clients: training.clients,
        candidates: training.candidates.map((candidate: any) => ({
          id: candidate.id,
          contact_details: candidate.contact_details,
          profile_picture: candidate.profile_picture,
        })),
        evaluations: evaluations.map((evaluation: any) => ({
          id: evaluation.id,
          name: evaluation.name,
          description: evaluation.description,
          stage: evaluation.stage,
          sourceFiles: evaluation.source_files,
        })),
        trainingModules: trainingModulesData.map((module: any) => ({
          id: module.id,
          name: module.name,
          description: module.description,
          status: module.status,
          trainingSessions: module.training_sessions.map((session: any) => ({
            id: session.id,
            name: session.name,
            deadline: session.deadline,
            status: session.status,
            sourceFiles: session.source_files,
            trainers: session.trainers,
            type: session.type,
            date: session.date,
            address: session.address,
            candidateTrainingSessions: session.candidate_training_sessions.map(
              (candSession: any) => ({
                ...candSession,
                trainingId: trainingId,
              }),
            ),
            trainingId: trainingId,
          })),
        })),
      };

      events.push(trainingEvent);
    });

    store.commit('setTrainings', events);
  },

  async getTrainingSessions({
    self,
    filter,
    reloadExisting,
  }: {
    self: any;
    filter: string;
    reloadExisting?: boolean;
  }): Promise<void> {
    if (store.state.allTrainingSessionsDelivered && !reloadExisting) return;
    store.commit('setTrainingSessionsRequestPending', true);
    const currentListLength = store.state.eventsNumber.trainingSessions[filter];
    const defaultCall = !reloadExisting;
    let page: number, perPage: number;

    if (defaultCall) {
      perPage = 10;
      page = store.state.nextTrainingSessionsPageNum;
    } else if (reloadExisting) {
      perPage = currentListLength + 1;
      page = 1;

      store.commit('cleanTrainingSessions');
    }
    if (defaultCall)
      store.commit(
        'setNextTrainingSessionsPageNum',
        (store.state.nextTrainingSessionsPageNum += 1),
      );
    const dateFilter = this.getDateFilter(filter);
    const response = await requestAPI({
      url: `/api/v1/training-sessions?${dateFilter}&page=${page}&per_page=${perPage}&order[created_at]=DESC`,
      method: 'GET',
    });

    if (response.ok) {
      const data = await response.json();
      const currentDate = new Date();
      await this.parseTrainingSessions(data, filter, currentDate);
      store.commit('setTrainingSessionsRequestPending', false);
    } else if (response.status !== 401) {
      self.$message.error(self.$t('someErrorOccurred'));
      store.commit('setTrainingSessionsRequestPending', false);
    }
  },

  async getClientTrainingSessions({
    self,
    filter,
    reloadExisting,
  }: {
    self: any;
    filter: string;
    reloadExisting?: boolean;
  }): Promise<void> {
    if (store.state.allSessionsDelivered && !reloadExisting) return;
    store.commit('setSessionsRequestPending', true);
    const currentListLength = store.state.eventsNumber.sessions[filter];
    const defaultCall = !reloadExisting;
    let page: number, perPage: number;
    let statuses: string;
    if (filter === 'new') statuses = 'status[]=new&status[]=pre_work&status[]=post_work';
    if (filter === 'complete') statuses = 'status[]=completed&status[]=cancelled';
    if (filter === 'active') statuses = 'status[]=in_progress';
    if (defaultCall) store.commit('setNextSessionsPageNum', (store.state.nextSessionsPageNum += 1));

    if (defaultCall) {
      perPage = 10;
      page = store.state.nextSessionsPageNum;
    } else if (reloadExisting) {
      perPage = currentListLength + 1;
      page = 1;

      store.commit('cleanSessions');
    }
    if (defaultCall) store.commit('setNextSessionsPageNum', (store.state.nextSessionsPageNum += 1));
    const response = await requestAPI({
      url: `/api/v1/training-sessions?${statuses}&order[created_at]=DESC`,
      method: 'GET',
    });

    if (response.ok) {
      const data = await response.json();
      await this.parseClientSessions(data);
      store.commit('setSessionsRequestPending', false);
    } else if (response.status !== 401) {
      self.$message.error(self.$t('someErrorOccurred'));
      store.commit('setSessionsRequestPending', false);
    }
  },

  getDateFilter(filter: string): string {
    const currentDate = new Date();
    const sevenDaysFromNow = new Date(currentDate);
    sevenDaysFromNow.setDate(currentDate.getDate() + 7);

    const fourteenDaysFromNow = new Date(currentDate);
    fourteenDaysFromNow.setDate(currentDate.getDate() + 14);

    let dateFilter = '';

    if (filter === 'passageOne') {
      dateFilter = `dateOrDeadline[before]=${sevenDaysFromNow.toISOString()}&dateOrDeadline[after]=${currentDate.toISOString()}`;
    } else if (filter === 'passageTwo') {
      const startDatePassageTwo = new Date(currentDate);
      startDatePassageTwo.setDate(currentDate.getDate() + 7);

      const endDatePassageTwo = new Date(currentDate);
      endDatePassageTwo.setDate(startDatePassageTwo.getDate() + 7);

      dateFilter = `dateOrDeadline[before]=${endDatePassageTwo.toISOString()}&dateOrDeadline[after]=${startDatePassageTwo.toISOString()}`;
    } else if (filter === 'passageThree') {
      const startDatePassageThree = new Date(currentDate);
      startDatePassageThree.setDate(currentDate.getDate() + 15);

      dateFilter = `dateOrDeadline[after]=${startDatePassageThree.toISOString()}`;
    }

    return dateFilter;
  },

  async parseTrainingSessions(data: any, filter: string, currentDate: Date): Promise<void> {
    const trainingSessions = [];

    data.forEach((session: any) => {
      const sessionDate = session.date ? new Date(session.date.date) : null;
      const sessionDeadline = session.deadline ? new Date(session.deadline) : null;
      const timeDifference = Math.ceil(
        ((sessionDate || sessionDeadline).getTime() - currentDate.getTime()) /
          (24 * 60 * 60 * 1000),
      );

      if (
        session.training_module &&
        session.training_module.training &&
        session.training_module.training.id &&
        ((filter === 'passageOne' && timeDifference >= 0 && timeDifference <= 7) ||
          (filter === 'passageTwo' && timeDifference >= 8 && timeDifference <= 15) ||
          (filter === 'passageThree' && timeDifference > 15) ||
          (filter === 'passageThree' && timeDifference === Infinity))
      ) {
        const trainingSession = {
          name: session.name,
          deadline: session?.deadline,
          status: session.status,
          sourceFiles: session.source_files,
          trainers: session.trainers,
          meetingType: session.type,
          date: session.deadline_or_date,
          address: session.address,
          id: session.id,
          description: session.description,
          tasks: session.candidate_training_sessions,
          trainingId: session.training_module.training.id,
        };

        trainingSessions.push(trainingSession);
      }
    });

    store.commit('setTrainingSessions', trainingSessions);
  },
  async parseClientSessions(data: any): Promise<void> {
    const sessions = [];

    data.forEach((session: any) => {
      const trainingSession = {
        name: session.name,
        deadline: session.deadline,
        status: session.status,
        sourceFiles: session.source_files,
        trainers: session.trainers,
        meetingType: session.type,
        date: session.deadline_or_date,
        address: session.address,
        id: session.id,
        description: session.description,
        tasks: session.candidate_training_sessions,
        trainingId: session.training_module.training.id,
      };
      sessions.push(trainingSession);
    });
    store.commit('setSessions', sessions);
  },

  async getTasks({
    self,
    filter,
    reloadExisting,
  }: {
    self: any;
    filter: string;
    reloadExisting?: boolean;
  }): Promise<void> {
    if (store.state.allTasksDelivered && !reloadExisting) return;
    store.commit('setTasksRequestPending', true);
    const currentListLength = store.state.eventsNumber.tasks[filter];
    const defaultCall = !reloadExisting;
    let page: number, perPage: number;
    if (defaultCall) {
      perPage = 10;
      page = store.state.nextTaskPageNum;
    } else if (reloadExisting) {
      perPage = currentListLength + 1;
      page = 1;

      store.commit('cleanTasks');
    }
    let statuses: string;
    if (filter === 'overallTasksNumber') statuses = '';
    if (filter === 'criticalTasksNumber') statuses = 'status[]=missed&status[]=rejected';
    if (filter === 'newTasksNumber') statuses = 'status[]=in_progress';
    if (filter === 'completeTasksNumber') statuses = 'status[]=accepted&status[]=results_uploaded';
    if (defaultCall) store.commit('setNextTasksPageNum', (store.state.nextTasksPageNum += 1));
    const response = await requestAPI({
      url: `/api/v1/candidate-training-sessions?${statuses}&order[updated_at]=DESC`,
      method: 'GET',
    });

    if (response.ok) {
      this.parseTasks(response, 'tasks', filter);
      store.commit('setTasksRequestPending', false);
    } else if (response.status !== 401) {
      self.$message.error(self.$t('someErrorOccurred'));
      store.commit('setTasksRequestPending', false);
    }
  },

  async parseTasks(response: { json: () => any }): Promise<void> {
    const data = await response.json();

    const tasks: any[] = [];

    data.forEach((session: any) => {
      const task = {
        id: session.id || null,
        candidate: session.candidate || null,
        accepted: session.accepted || false,
        attended: session.attended || false,
        status: session.status || null,
        stage: session.stage || null,
        results: session.results || null,
        rejectedBy: session.rejected_by || null,
        trainingSessionId: session.training_session.id || null,
        trainingId: session.training_session.training_module.training.id || null,
      };
      tasks.push(task);
    });

    store.commit('setTrainingTasks', tasks);
  },
  async getCandidateTrainings(self: any): Promise<void> {
    const response = await requestAPI({
      url: `/api/v1/trainings`,
      method: 'GET',
    });

    if (response.ok) {
      this.parseCandidateTrainings(response);
    } else if (response.status !== 401) self.$message.error(self.$t('someErrorOccurred'));
  },

  async parseCandidateTrainings(response: { json: () => any }): Promise<void> {
    const data = await response.json();

    const events: any[] = [];

    data.forEach((training: any) => {
      const trainingId = {
        id: training.id,
      };

      events.push(trainingId);
    });

    store.commit('setCandidateTrainings', events);
  },
  async checkClientOnTrainingPathTrainings(self: any): Promise<void> {
    const response = await requestAPI({
      url: `/api/v1/trainings`,
      method: 'GET',
    });
    if (response.ok) {
      const numberOfClientTrainings = await response.json();
      const clientHasTrainings = numberOfClientTrainings.length > 0;
      if (clientHasTrainings) {
        store.commit('setNumberOfClientTrainings', numberOfClientTrainings.length);
      }
    } else if (response.status !== 401) {
      self.$message.error(self.$t('someErrorOccurred'));
    }
  },
};

export default trainingService;
